const DEFAULT_SWING_LOOKBACK = 5;
const EXTERNAL_LOOKBACK_MULTIPLIER = 3;
const MAX_VISIBLE_BOS_EVENTS = 10;

function averageRange(candles, lookback = 100) {
  if (!candles.length) return 0;

  const slice = candles.slice(-lookback);
  const total = slice.reduce((sum, candle) => sum + (candle.high - candle.low), 0);

  return total / slice.length;
}

function getAnalyzedCandles(candles) {
  if (!Array.isArray(candles) || candles.length === 0) {
    return [];
  }

  const lastCandle = candles[candles.length - 1];

  if (lastCandle?.isClosed === false) {
    return candles.slice(0, -1);
  }

  return candles;
}

function detectConfirmedSwings(candles, swingLookback) {
  const swings = [];

  for (
    let index = swingLookback;
    index < candles.length - swingLookback;
    index += 1
  ) {
    let isHigh = true;
    let isLow = true;

    for (let offset = 1; offset <= swingLookback; offset += 1) {
      if (candles[index].high <= candles[index - offset].high) isHigh = false;
      if (candles[index].high <= candles[index + offset].high) isHigh = false;

      if (candles[index].low >= candles[index - offset].low) isLow = false;
      if (candles[index].low >= candles[index + offset].low) isLow = false;
    }

    if (isHigh) {
      swings.push({
        type: "HIGH",
        index,
        time: candles[index].time,
        price: candles[index].high,
        confirmedIndex: index + swingLookback,
      });
    }

    if (isLow) {
      swings.push({
        type: "LOW",
        index,
        time: candles[index].time,
        price: candles[index].low,
        confirmedIndex: index + swingLookback,
      });
    }
  }

  return swings;
}

function reduceToMajorSwings(swings) {
  return swings.reduce((majorSwings, swing) => {
    const previousSwing = majorSwings[majorSwings.length - 1];

    if (!previousSwing) {
      majorSwings.push(swing);
      return majorSwings;
    }

    if (previousSwing.type !== swing.type) {
      majorSwings.push(swing);
      return majorSwings;
    }

    const isMoreExtreme =
      swing.type === "HIGH"
        ? swing.price >= previousSwing.price
        : swing.price <= previousSwing.price;

    if (isMoreExtreme) {
      majorSwings[majorSwings.length - 1] = swing;
    }

    return majorSwings;
  }, []);
}

function attachSwingLineTargets(swings, bosEvents, lastCandleTime) {
  return swings.map((swing) => {
    const breakingEvent = bosEvents.find(
      (event) => event.swingIndex === swing.index
    );

    return {
      ...swing,
      lineEndTime: breakingEvent?.bosCandleTime ?? lastCandleTime,
      isBroken: Boolean(breakingEvent),
    };
  });
}

function buildExternalMajorSwings(candles, swingLookback) {
  const externalLookback = Math.max(
    swingLookback + 2,
    swingLookback * EXTERNAL_LOOKBACK_MULTIPLIER
  );
  const confirmedExternalSwings = detectConfirmedSwings(candles, externalLookback);
  const averageCandleRange = averageRange(candles);
  const minStructureDistance =
    averageCandleRange * Math.max(2.5, externalLookback * 0.4);

  const filteredSwings = reduceToMajorSwings(confirmedExternalSwings).reduce(
    (majorSwings, swing) => {
      const previousSwing = majorSwings[majorSwings.length - 1];

      if (!previousSwing) {
        majorSwings.push(swing);
        return majorSwings;
      }

      if (previousSwing.type === swing.type) {
        const isMoreExtreme =
          swing.type === "HIGH"
            ? swing.price >= previousSwing.price
            : swing.price <= previousSwing.price;

        if (isMoreExtreme) {
          majorSwings[majorSwings.length - 1] = swing;
        }

        return majorSwings;
      }

      if (Math.abs(swing.price - previousSwing.price) < minStructureDistance) {
        return majorSwings;
      }

      majorSwings.push(swing);
      return majorSwings;
    },
    []
  );

  return {
    majorSwings: filteredSwings,
    externalLookback,
  };
}

function detectBosEvents(candles, swings) {
  const bosEvents = [];
  let swingCursor = 0;
  let lastSwingHigh = null;
  let lastSwingLow = null;

  for (let candleIndex = 0; candleIndex < candles.length; candleIndex += 1) {
    while (
      swingCursor < swings.length &&
      swings[swingCursor].confirmedIndex <= candleIndex
    ) {
      const swing = swings[swingCursor];

      if (swing.type === "HIGH") {
        lastSwingHigh = { ...swing, broken: false };
      } else {
        lastSwingLow = { ...swing, broken: false };
      }

      swingCursor += 1;
    }

    const candle = candles[candleIndex];

    if (
      lastSwingHigh &&
      !lastSwingHigh.broken &&
      candle.close > lastSwingHigh.price
    ) {
      bosEvents.push({
        event: "BOS",
        type: "UP",
        direction: "BULLISH",
        price: lastSwingHigh.price,
        timestamp: candle.time,
        swingIndex: lastSwingHigh.index,
        swingTime: lastSwingHigh.time,
        bosCandleIndex: candleIndex,
        bosCandleTime: candle.time,
        closePrice: candle.close,
      });

      lastSwingHigh = { ...lastSwingHigh, broken: true };
    }

    if (
      lastSwingLow &&
      !lastSwingLow.broken &&
      candle.close < lastSwingLow.price
    ) {
      bosEvents.push({
        event: "BOS",
        type: "DOWN",
        direction: "BEARISH",
        price: lastSwingLow.price,
        timestamp: candle.time,
        swingIndex: lastSwingLow.index,
        swingTime: lastSwingLow.time,
        bosCandleIndex: candleIndex,
        bosCandleTime: candle.time,
        closePrice: candle.close,
      });

      lastSwingLow = { ...lastSwingLow, broken: true };
    }
  }

  return {
    bosEvents,
    lastSwingHigh,
    lastSwingLow,
  };
}

function getMarketMatchedEvent(bosEvents, market) {
  const expectedDirection =
    market === "BOOM" ? "BULLISH" : market === "CRASH" ? "BEARISH" : null;

  if (!expectedDirection) {
    return bosEvents[bosEvents.length - 1] || null;
  }

  for (let index = bosEvents.length - 1; index >= 0; index -= 1) {
    if (bosEvents[index].direction === expectedDirection) {
      return bosEvents[index];
    }
  }

  return null;
}

function filterBosEventsByMarket(bosEvents, market) {
  if (market === "BOOM") {
    return bosEvents.filter((event) => event.direction === "BULLISH");
  }

  if (market === "CRASH") {
    return bosEvents.filter((event) => event.direction === "BEARISH");
  }

  return bosEvents;
}

export function detectStructure(candles, market, options = {}) {
  const swingLookback =
    options.swingLookback && options.swingLookback > 0
      ? options.swingLookback
      : DEFAULT_SWING_LOOKBACK;

  const analyzedCandles = getAnalyzedCandles(candles);

  if (analyzedCandles.length < swingLookback * 4) {
    return {
      event: null,
      reason: "Not enough confirmed candles",
      majorSwings: [],
      bosEvents: [],
      swingLookback,
    };
  }

  const { majorSwings, externalLookback } = buildExternalMajorSwings(
    analyzedCandles,
    swingLookback
  );
  const { bosEvents, lastSwingHigh, lastSwingLow } = detectBosEvents(
    analyzedCandles,
    majorSwings
  );
  const filteredBosEvents = filterBosEventsByMarket(bosEvents, market).slice(
    -MAX_VISIBLE_BOS_EVENTS
  );
  const annotatedSwings = attachSwingLineTargets(
    majorSwings,
    filteredBosEvents,
    analyzedCandles[analyzedCandles.length - 1]?.time ?? null
  );

  const latestEvent = filteredBosEvents[filteredBosEvents.length - 1] || null;
  const matchedEvent = getMarketMatchedEvent(filteredBosEvents, market);

  return {
    event: matchedEvent ? "BOS" : null,
    direction: matchedEvent?.direction || null,
    bosPrice: matchedEvent?.price || null,
    bosIndex: matchedEvent?.bosCandleIndex ?? null,
    bosCandleIndex: matchedEvent?.bosCandleIndex ?? null,
    bosCandleTime: matchedEvent?.bosCandleTime ?? null,
    swingIndex: matchedEvent?.swingIndex ?? null,
    reason: matchedEvent
      ? matchedEvent.direction === "BULLISH"
        ? "Confirmed close above external major swing high"
        : "Confirmed close below external major swing low"
      : "Waiting for external major BOS",
    swingLookback,
    externalLookback,
    analyzedCount: analyzedCandles.length,
    majorSwings: annotatedSwings,
    bosEvents: filteredBosEvents,
    latestEvent,
    lastSwingHigh,
    lastSwingLow,
    lastAnalyzedTime: analyzedCandles[analyzedCandles.length - 1]?.time ?? null,
  };
}

export {
  DEFAULT_SWING_LOOKBACK,
  MAX_VISIBLE_BOS_EVENTS,
  getAnalyzedCandles,
  detectConfirmedSwings,
  reduceToMajorSwings,
};
